import {today} from 'common/date';
import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {Button} from 'src/Button';
import {
  DateRangeFilter,
  useDateRangeFilter,
} from 'src/components/DateRangeFilter';
import {
  buildEquitySeries,
  formatCents,
  issuerCurrency,
  latestMark,
  type EquityIssuerData,
  type EquityPoint,
  type PriceBasis,
} from 'src/EquityView/equitySeries';
import {niceTimeTicks, spanDateFormatter} from 'src/lib/chartDateAxis';

// Whole units for the big numbers, cents once a value is small enough that
// rounding would misstate it (a 61-cent position is not "$1").
const dollars = (value: number, currency: string): string =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: value !== 0 && Math.abs(value) < 10 ? 2 : 0,
  });

const dateLabel = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/** Green $ badge on the actual line wherever a sale happened. */
const SaleDot = (props: {
  cx?: number;
  cy?: number;
  payload?: EquityPoint;
}) => {
  const {cx, cy, payload} = props;
  if (cx == null || cy == null || payload?.sale == null) return null;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy - 14}
        r={8}
        fill="var(--chart-3)"
        stroke="var(--color-background)"
        strokeWidth={2}
      />
      <text
        x={cx}
        y={cy - 10.5}
        textAnchor="middle"
        fontSize={10}
        fontWeight={700}
        fill="var(--color-background)"
      >
        $
      </text>
      <line
        x1={cx}
        y1={cy - 6}
        x2={cx}
        y2={cy - 3}
        stroke="var(--chart-3)"
        strokeWidth={2}
        opacity={0.6}
      />
    </g>
  );
};

const SERIES: Array<{
  key: 'neverSold' | 'realizedHeld' | 'actual';
  name: string;
  color: string;
  dashed: boolean;
}> = [
  {
    key: 'neverSold',
    name: 'If never sold',
    color: 'var(--color-muted-foreground)',
    dashed: true,
  },
  {
    key: 'realizedHeld',
    name: 'Realized + held',
    color: 'var(--chart-2)',
    dashed: false,
  },
  {key: 'actual', name: 'Actual holdings', color: 'var(--chart-1)', dashed: false},
];

const ChartTooltip = ({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{payload: EquityPoint}>;
  currency: string;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="border-border bg-popover text-popover-foreground rounded-md border p-2 font-mono text-xs shadow-md">
      <div className="mb-1 font-semibold">{dateLabel(point.timestamp)}</div>
      {SERIES.map((series) => (
        <div key={series.key} className="flex items-center gap-1.5">
          <span
            className="inline-block w-3.5 border-t-2"
            style={{
              borderColor: series.color,
              borderTopStyle: series.dashed ? 'dashed' : 'solid',
            }}
          />
          <span>
            {series.name} {dollars(point[series.key], currency)}
          </span>
        </div>
      ))}
      {point.fmv != null && (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3.5 border-t-2"
            style={{borderColor: 'var(--chart-4)'}}
          />
          <span>
            FMV{' '}
            {point.fmv.toLocaleString('en-US', {
              style: 'currency',
              currency,
              maximumFractionDigits: 6,
            })}
            /sh
          </span>
        </div>
      )}
      {point.sale && (
        <div className="mt-1 flex items-center gap-1.5">
          <span
            className="inline-block size-2.5 rounded-full"
            style={{background: 'var(--chart-3)'}}
          />
          <span className="font-semibold">
            {point.sale.name}: sold {point.sale.shares.toLocaleString('en-US')}{' '}
            sh
            {point.sale.grossCents != null
              ? ` → ${formatCents(point.sale.grossCents, currency)}`
              : ''}
          </span>
        </div>
      )}
    </div>
  );
};

const PRICE_BASIS_OPTIONS: Array<{value: PriceBasis; label: string}> = [
  {value: 'atTheTime', label: 'Price at the time'},
  {value: 'today', label: "Today's price"},
];

/**
 * The value chart: three step lines (never-sold counterfactual, realized +
 * held, actual holdings) with sale badges, over a synced FMV-per-share
 * strip. The price toggle revalues held shares at today's latest mark —
 * vests jump by what they're worth now and each sale shows exactly the
 * value it left behind.
 */
export const EquityChartPanel = ({issuer}: {issuer: EquityIssuerData}) => {
  const {dateRange, setDateRange} = useDateRangeFilter('ALL');
  const [priceBasis, setPriceBasis] = React.useState<PriceBasis>('atTheTime');
  const todaySeconds = React.useMemo(() => today().toSeconds(), []);

  const allPoints = React.useMemo(
    () => buildEquitySeries(issuer, {priceBasis, todaySeconds}),
    [issuer, priceBasis, todaySeconds],
  );
  const points = React.useMemo(() => {
    // The picker's DateTimes live in the browser's zone; the points sit at
    // midnight UTC. Compare calendar days, not instants, or a boundary
    // point slips out of the window on either side of UTC.
    const utcDay = (value: {year: number; month: number; day: number}) =>
      Date.UTC(value.year, value.month - 1, value.day);
    const startMs = dateRange.startDate ? utcDay(dateRange.startDate) : null;
    const endMs = dateRange.endDate ? utcDay(dateRange.endDate) : null;
    const inside = allPoints.filter(
      (point) =>
        (startMs == null || point.timestamp >= startMs) &&
        (endMs == null || point.timestamp <= endMs),
    );
    // A window cut between ledger dates still has a position: carry the
    // last preceding point onto each boundary so the steps enter (and, for
    // a historical end date, leave) at the right height — a quiet custom
    // window is a flat line, not "not enough history". Boundaries clamp to
    // the last real point (today): the series deliberately ends there, so
    // no synthetic point may extend it into the future, and a wholly
    // future window charts nothing.
    const lastRealTs = allPoints[allPoints.length - 1]?.timestamp ?? null;
    const carryTo = (boundaryMs: number) => {
      const carried = [...allPoints]
        .reverse()
        .find((point) => point.timestamp < boundaryMs);
      return carried ? {...carried, timestamp: boundaryMs, sale: null} : null;
    };
    if (
      startMs != null &&
      lastRealTs != null &&
      startMs <= lastRealTs &&
      (inside.length === 0 || inside[0].timestamp > startMs)
    ) {
      const carried = carryTo(startMs);
      if (carried) inside.unshift(carried);
    }
    if (endMs != null && lastRealTs != null) {
      const endBoundary = Math.min(endMs, lastRealTs);
      if (inside.length === 0 || inside[inside.length - 1].timestamp < endBoundary) {
        const carried = carryTo(endBoundary + 1);
        if (carried) inside.push({...carried, timestamp: endBoundary});
      }
    }
    return inside;
  }, [allPoints, dateRange]);

  const latest = latestMark(issuer, todaySeconds);
  const currency = issuerCurrency(issuer);

  // Controls render regardless — a range with too little to chart must stay
  // reversible from the range picker itself.
  const chartable = points.length >= 2;
  const firstTs = chartable ? points[0].timestamp : 0;
  const lastTs = chartable ? points[points.length - 1].timestamp : 0;
  const ticks = niceTimeTicks(firstTs, lastTs);
  const formatTick = spanDateFormatter(firstTs, lastTs);
  const axisProps = {
    dataKey: 'timestamp' as const,
    type: 'number' as const,
    scale: 'time' as const,
    domain: ['dataMin', 'dataMax'] as [string, string],
    ticks,
    tickFormatter: formatTick,
    tick: {fill: 'var(--color-muted-foreground)', fontSize: 12},
    tickLine: {stroke: 'var(--color-border)'},
    axisLine: {stroke: 'var(--color-border)'},
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2 px-1">
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        <div className="flex items-center gap-1">
          {PRICE_BASIS_OPTIONS.map((option) => (
            <Button
              key={option.value}
              className="px-2.5 py-1 text-xs"
              variant={priceBasis === option.value ? 'primary' : 'outline'}
              onClick={() => setPriceBasis(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <span className="text-muted-foreground ml-auto flex items-center gap-1.5 text-xs">
          <span
            className="inline-block size-2.5 rounded-full"
            style={{background: 'var(--chart-3)'}}
          />
          $ = sale
        </span>
      </div>
      {!chartable && (
        <div className="text-muted-foreground p-6 text-sm">
          Not enough history to chart in this window — widen the range, or
          record a couple of marks first.
        </div>
      )}
      {chartable && (
        <>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={points} syncId="equity" margin={{top: 18, right: 12}}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-border)"
            strokeWidth={1.5}
          />
          <XAxis {...axisProps} />
          <YAxis
            tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
            tickFormatter={(value: number) => dollars(value, currency)}
            tickLine={{stroke: 'var(--color-border)'}}
            axisLine={{stroke: 'var(--color-border)'}}
            width={80}
          />
          <Tooltip content={<ChartTooltip currency={currency} />} />
          <Legend wrapperStyle={{paddingTop: '8px'}} />
          <Line
            type="stepAfter"
            dataKey="neverSold"
            name="If never sold"
            stroke="var(--color-muted-foreground)"
            strokeDasharray="5 4"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="stepAfter"
            dataKey="realizedHeld"
            name="Realized + held"
            stroke="var(--chart-2)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="stepAfter"
            dataKey="actual"
            name="Actual holdings"
            stroke="var(--chart-1)"
            strokeWidth={2}
            dot={<SaleDot />}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="text-muted-foreground px-1 text-xs">
        FMV per share{latest ? ` — latest ${latest.price.format()}` : ''}
      </div>
      <ResponsiveContainer width="100%" height={110}>
        <AreaChart data={points} syncId="equity" margin={{right: 12}}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-border)"
            strokeWidth={1.5}
          />
          <XAxis {...axisProps} />
          <YAxis
            tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
            tickFormatter={(value: number) =>
              // Marks can sit well under a currency unit ($0.60875); whole-
              // unit ticks would all read $0/$1 on such an axis.
              value.toLocaleString('en-US', {
                style: 'currency',
                currency,
                maximumFractionDigits: value > 0 && value < 10 ? 2 : 0,
              })
            }
            tickLine={{stroke: 'var(--color-border)'}}
            axisLine={{stroke: 'var(--color-border)'}}
            width={80}
          />
          <Tooltip content={() => null} />
          <Area
            type="stepAfter"
            dataKey="fmv"
            name="FMV per share"
            stroke="var(--chart-4)"
            strokeWidth={2}
            fill="var(--chart-4)"
            fillOpacity={0.12}
            connectNulls
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
        </>
      )}
    </div>
  );
};
